import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import axios from "axios";
import { requestOtp, verifyOtp } from "../features/auth/authSlice";
import { api } from "../utils/apiUtils";
import { API_URL } from "../config/apiConfig";
import { FaTimes, FaGoogle, FaSms, FaArrowLeft, FaSpinner } from "react-icons/fa";

const LoginModal = ({ isOpen, onClose, onSuccess }) => {
  const [otpRole, setOtpRole] = useState("user");
  const [phone, setPhone] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState("input"); // "input" or "verify"
  const [otp, setOtp] = useState(["", "", "", ""]);
  const [timeLeft, setTimeLeft] = useState(120);
  const [canResend, setCanResend] = useState(false);
  const otpInputs = useRef([]);
  const dispatch = useDispatch();
  const [loginImages, setLoginImages] = useState([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Fetch login promotional images
  useEffect(() => {
    const fetchLoginImages = async () => {
      try {
        const response = await axios.get(`${API_URL}/auth-images`, {
          params: { type: 'user_login' }
        });
        if (response.data.success && response.data.data.length > 0) {
          setLoginImages(response.data.data);
        }
      } catch (error) {
        console.error("Error fetching login images:", error);
      }
    };
    if (isOpen) {
      fetchLoginImages();
    }
  }, [isOpen]);

  // Auto-rotate images every 5 seconds if multiple exist
  useEffect(() => {
    if (loginImages.length > 1) {
      const timer = setInterval(() => {
        setCurrentImageIndex((prev) => (prev + 1) % loginImages.length);
      }, 5000);
      return () => clearInterval(timer);
    }
  }, [loginImages]);

  // Timer countdown effect for OTP resend
  useEffect(() => {
    if (timeLeft <= 0) {
      setCanResend(true);
      return;
    }
    const timer = setInterval(() => {
      setTimeLeft((prev) => prev - 1);
    }, 1000);
    return () => clearInterval(timer);
  }, [timeLeft]);

  // Handle ESC key
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === "Escape") onClose();
    };
    if (isOpen) {
      window.addEventListener("keydown", handleEsc);
    }
    return () => window.removeEventListener("keydown", handleEsc);
  }, [isOpen, onClose]);

  const handleOverlayClick = (e) => {
    if (e.target.classList.contains('login-modal-overlay')) {
      onClose();
    }
  };

  const handlePhoneChange = (e) => {
    const value = e.target.value.replace(/\D/g, "");
    if (value.length <= 10) {
      setPhone(value);
    }
  };

  const handleOtpChange = (value, index) => {
    if (!/^[0-9]?$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    if (value && index < 3) {
      otpInputs.current[index + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (e, index) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      otpInputs.current[index - 1]?.focus();
    } else if (e.key === "Enter") {
      e.preventDefault();
      handleVerifyOtp(e);
    }
  };

  // Format time remaining for display
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  const resetToPhoneInput = () => {
    setCurrentStep("input");
    setOtp(["", "", "", ""]);
    setTimeLeft(120);
    setCanResend(false);
  };

  const handleRequestOtp = async (e) => {
    e.preventDefault();

    if (!phone) {
      toast.error("Please enter your phone number");
      return;
    }

    if (!/^[0-9]{10}$/.test(phone)) {
      toast.error("Please enter a valid 10-digit mobile number");
      return;
    }

    setIsLoading(true);

    if (otpRole === "corporate_staff") {
      try {
        // Use unified API endpoint with role parameter
        const res = await api.post("/auth/request-otp", { phone, role: "staff" });
        if (res?.data?.success) {
          setCurrentStep("verify");
          setTimeLeft(120);
          setCanResend(false);
        } else {
          // Handle specific error codes
          const errorCode = res?.data?.code;
          const errorMessage = res?.data?.message || "Failed to send OTP";

          if (errorCode === "GLOBAL_PHONE_DUPLICATE") {
            toast.error(`${errorMessage}. Please contact support for assistance.`);
          } else {
            toast.error(errorMessage);
          }
        }
      } catch (error) {
        const errorCode = error?.response?.data?.code;
        const errorMessage = error?.response?.data?.message || error.message || "Network error. Please try again.";

        if (errorCode === "GLOBAL_PHONE_DUPLICATE") {
          toast.error(`${errorMessage}. Please contact support for assistance.`);
        } else {
          toast.error(errorMessage);
        }
      } finally {
        setIsLoading(false);
      }
      return;
    }

    try {
      const result = await dispatch(requestOtp({ phone }));

      if (requestOtp.fulfilled.match(result)) {
        setCurrentStep("verify");
        setTimeLeft(120);
        setCanResend(false);
      } else {
        // Check if error is due to phone being registered as staff
        const errorPayload = result.payload || {};
        const errorCode = typeof errorPayload === "object" ? errorPayload.code : null;
        const errorMessage = typeof errorPayload === "object" ? errorPayload.message : errorPayload;

        if (errorCode === "GLOBAL_PHONE_DUPLICATE") {
          toast.error("This phone number is registered as a Corporate Staff account. Please switch to Corporate Account tab.");
          // Auto-switch to corporate staff tab to help user
          setOtpRole("corporate_staff");
          setPhone("");
          setCurrentStep("input");
        } else {
          toast.error(errorMessage || "Failed to send OTP");
        }
      }
    } catch (error) {
      toast.error("Network error. Please try again.");
      console.error("OTP request error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    const otpString = otp.join("");

    if (!otpString) {
      toast.error("Please enter the OTP");
      return;
    }

    if (!/^[0-9]{4}$/.test(otpString)) {
      toast.error("Please enter a valid 4-digit OTP");
      return;
    }

    setIsLoading(true);

    if (otpRole === "corporate_staff") {
      try {
        // Use unified API endpoint with role parameter
        const res = await api.post("/auth/verify-otp", { phone, otp: otpString, role: "staff" });
        if (res?.data?.success) {
          const corporateToken = res?.data?.corporateToken || res?.data?.token;
          if (corporateToken) {
            localStorage.setItem("corporateToken", corporateToken);
          }
          const token = res?.data?.token;
          if (token) localStorage.setItem("token", token);
          const member = res?.data?.data || {};
          localStorage.setItem("corporateMember", JSON.stringify(member));
          localStorage.removeItem("user");
          dispatch({ type: "auth/reset" }); // reset redux auth state if needed
          sessionStorage.setItem("corporateBookingMode", "1");
          window.dispatchEvent(new Event("corporateSessionChanged"));
          onClose();
          if (onSuccess) {
            onSuccess();
          } else {
            window.location.reload();
          }
        } else {
          // Handle specific error codes
          const errorCode = res?.data?.code;
          const errorMessage = res?.data?.message || "Invalid OTP";

          if (errorCode === "GLOBAL_PHONE_DUPLICATE") {
            toast.error(`${errorMessage}. Please contact support for assistance.`);
          } else {
            toast.error(errorMessage);
          }
        }
      } catch (error) {
        const errorCode = error?.response?.data?.code;
        const errorMessage = error?.response?.data?.message || error.message || "Network error. Please try again.";

        if (errorCode === "GLOBAL_PHONE_DUPLICATE") {
          toast.error(`${errorMessage}. Please contact support for assistance.`);
        } else {
          toast.error(errorMessage);
        }
      } finally {
        setIsLoading(false);
      }
      return;
    }

    try {
      const result = await dispatch(verifyOtp({ phone, otp: otpString }));

      if (verifyOtp.fulfilled.match(result)) {
        onClose();
        if (onSuccess) {
          onSuccess();
        }
        // Reset form state
        setCurrentStep("input");
        setOtp(["", "", "", ""]);
        setPhone("");
      } else {
        // Check if error is due to phone being registered as staff
        const errorPayload = result.payload || {};
        const errorCode = typeof errorPayload === "object" ? errorPayload.code : null;
        const errorMessage = typeof errorPayload === "object" ? errorPayload.message : errorPayload;

        if (errorCode === "GLOBAL_PHONE_DUPLICATE") {
          toast.error("This phone number is registered as a Corporate Staff account. Please switch to Corporate Account tab.");
          // Auto-switch to corporate staff tab to help user
          setOtpRole("corporate_staff");
          setCurrentStep("input");
          setPhone("");
          setOtp(["", "", "", ""]);
        } else {
          toast.error(errorMessage || "Invalid OTP");
        }
      }
    } catch (error) {
      toast.error("Network error. Please try again.");
      console.error("OTP verification error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <div 
      className="login-modal-overlay fixed inset-0 bg-black/60 flex items-center justify-center z-[9999] p-2 sm:p-4 backdrop-blur-sm"
      onClick={handleOverlayClick}
    >
      <div className="login-modal-card bg-white rounded-2xl shadow-2xl w-full max-w-[340px] md:max-w-[900px] flex flex-col md:flex-row overflow-visible relative">
        {/* Close Button - Outside/Corner Style */}
        <button
          onClick={onClose}
          className="absolute -top-3 -right-3 md:-top-4 md:-right-6 text-white hover:scale-110 transition bg-black/80 rounded-full p-1.5 md:p-2 z-50 shadow-lg border-2 border-white"
          aria-label="Close"
        >
          <FaTimes className="text-[16px] md:text-[20px]" />
        </button>

        {/* Left Side - Promotional Image */}
        <div className="hidden md:block w-[45%] relative overflow-hidden min-h-[420px]">
          {loginImages.length > 0 ? (
            <div className="absolute inset-0">
              <img
                src={loginImages[currentImageIndex].imageUrl}
                alt={loginImages[currentImageIndex].title || "Login Promotion"}
                className="w-full h-full object-cover transition-opacity duration-1000"
              />
            </div>
          ) : (
            <div className="absolute inset-0 flex items-center justify-center bg-blue-50">
               <FaSpinner className="animate-spin text-blue-600 text-3xl" />
            </div>
          )}
        </div>

        {/* Right Side - Form */}
        <div className="w-full md:w-[55%] p-4 sm:p-8 md:p-8 relative bg-white flex flex-col">
          {/* Account Type Tabs - MMT Style */}
          <div className="account-type-tabs mb-4 md:mb-6">
            <div className="flex bg-[#F2F2F2] rounded-full p-1 border border-gray-200 shadow-inner">
              <button
                type="button"
                onClick={() => {
                  setOtpRole("user");
                  setPhone("");
                  resetToPhoneInput();
                }}
                className={`flex-1 py-3 px-2 sm:px-6 rounded-full text-[10px] sm:text-xs font-black tracking-widest transition-all duration-300 ${
                  otpRole === "user"
                    ? "bg-blue-600 text-white shadow-md transform scale-[1.02]"
                    : "text-gray-500 hover:text-gray-700 font-bold"
                }`}
              >
                PERSONAL ACCOUNT
              </button>
              <button
                type="button"
                onClick={() => {
                  setOtpRole("corporate_staff");
                  setPhone("");
                  resetToPhoneInput();
                }}
                className={`flex-1 py-3 px-2 sm:px-6 rounded-full text-[10px] sm:text-xs font-black tracking-widest transition-all duration-300 ${
                  otpRole === "corporate_staff"
                    ? "bg-blue-600 text-white shadow-md transform scale-[1.02]"
                    : "text-gray-500 hover:text-gray-700 font-bold"
                }`}
              >
                CORPORATE ACCOUNT
              </button>
            </div>
          </div>

          {currentStep === "input" && (
            <div className="flex-1 flex flex-col justify-center">
              {/* Phone Input */}
              <form onSubmit={handleRequestOtp} className="space-y-6">
                <div>
                  <label htmlFor="login-phone" className="block text-sm font-bold text-gray-700 mb-4 px-1">
                    Mobile Number
                  </label>
                  <div className={`phone-input-wrapper flex items-center border-[1.5px] rounded-xl overflow-hidden transition-all duration-300 ${phone && phone.length !== 10 ? 'border-red-400' : 'border-gray-300 focus-within:border-blue-600 focus-within:ring-4 focus-within:ring-blue-100'}`}>
                    <div className="country-code flex items-center gap-1.5 pl-3 pr-2 py-3 md:px-4 md:py-4 bg-white">
                      <svg className="flag-icon w-6 h-4 shadow-sm" viewBox="0 0 900 600" xmlns="http://www.w3.org/2000/svg">
                        <rect width="900" height="200" fill="#FF9933"/>
                        <rect y="200" width="900" height="200" fill="#FFFFFF"/>
                        <rect y="400" width="900" height="200" fill="#138808"/>
                        <circle cx="450" cy="300" r="60" fill="#1F41B6"/>
                      </svg>
                      <span className="country-dial text-sm font-bold text-gray-800">+91</span>
                      <svg className="w-4 h-4 text-blue-600 ml-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                      </svg>
                    </div>
                    <input
                      type="tel"
                      id="login-phone"
                      value={phone}
                      onChange={handlePhoneChange}
                      className="flex-1 py-3 md:py-4 px-1.5 border-0 focus:outline-none focus:ring-0 text-sm sm:text-base md:text-xl font-bold text-gray-800 tracking-normal md:tracking-wider placeholder:text-gray-300 placeholder:font-normal"
                      placeholder="Enter Mobile Number"
                      maxLength="10"
                    />
                  </div>
                  {phone && phone.length !== 10 && (
                    <p className="text-red-500 text-[11px] font-bold mt-2 px-1 flex items-center gap-1">
                      <FaTimes className="text-[10px]" /> Please enter a valid Mobile Number.
                    </p>
                  )}
                </div>

                {/* Continue Button */}
                <button
                  type="submit"
                  disabled={isLoading || phone.length !== 10}
                  className={`continue-btn w-full py-3 md:py-4 px-4 rounded-full font-black text-white text-base md:text-lg tracking-widest transition-all duration-300 shadow-xl active:scale-95 ${
                    isLoading || phone.length !== 10
                      ? "bg-[#EEF1F5] text-[#A1A5B7] cursor-not-allowed shadow-none"
                      : "bg-gradient-to-r from-blue-500 to-blue-700 hover:from-blue-600 hover:to-blue-800"
                  }`}
                >
                  {isLoading ? "SENDING..." : "CONTINUE"}
                </button>
              </form>

              {/* Or Login/Signup With Section */}
              <div className="flex items-center gap-3 my-3 md:my-6">
                <div className="flex-1 h-px bg-gray-100"></div>
                <span className="text-xs text-gray-400 font-bold tracking-widest uppercase">Or Login/Signup With</span>
                <div className="flex-1 h-px bg-gray-100"></div>
              </div>

              {/* Social Login Icons - Circle Style */}
              <div className="flex justify-center gap-6 mb-1 md:mb-2">
                <button
                  type="button"
                  onClick={() => toast.info("Google login coming soon")}
                  className="w-14 h-14 rounded-full border border-gray-100 flex items-center justify-center hover:shadow-lg hover:border-blue-200 hover:bg-white transition-all duration-300 shadow-md transform hover:-translate-y-1"
                >
                  <img src="https://cdn.cdnlogo.com/logos/g/35/google-icon.svg" className="w-6 h-6" alt="Google" />
                </button>
              </div>
            </div>
          )}

          {/* OTP Verification Step */}
          {currentStep === "verify" && (
            <div className="flex-1 flex flex-col justify-center">
              <h2 className="text-3xl font-black mb-2 text-gray-800">Verify OTP</h2>
              <p className="text-gray-500 flex items-center gap-2 mb-8 text-sm font-medium">
                Enter 4 digit OTP sent to
                <button
                  type="button"
                  onClick={resetToPhoneInput}
                  className="flex items-center gap-1 text-blue-600 hover:underline font-bold"
                >
                  +91 {phone}
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/></svg>
                </button>
              </p>

              <div className="flex justify-start mb-8 gap-4">
                {otp.map((digit, index) => (
                  <input
                    key={index}
                    ref={(el) => (otpInputs.current[index] = el)}
                    type="text"
                    value={digit}
                    maxLength="1"
                    onChange={(e) => handleOtpChange(e.target.value, index)}
                    onKeyDown={(e) => handleOtpKeyDown(e, index)}
                    className={`w-14 h-14 border-2 rounded-xl text-center text-2xl font-black outline-none transition-all duration-300 ${digit ? 'border-blue-600 bg-blue-50/30' : 'border-gray-200 focus:border-blue-600'}`}
                    inputMode="numeric"
                    autoComplete="off"
                    disabled={isLoading}
                  />
                ))}
              </div>

              <div className="mb-10 text-sm font-medium text-gray-500">
                Didn't get OTP? {canResend ? (
                  <button onClick={handleRequestOtp} className="text-blue-600 font-bold hover:underline">Resend Now</button>
                ) : (
                  <span>Resend in <span className="text-blue-600 font-black">{formatTime(timeLeft)}</span></span>
                )}
              </div>

              <form onSubmit={handleVerifyOtp}>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full py-4 rounded-full bg-blue-600 hover:bg-blue-700 text-white font-black tracking-widest transition-all duration-300 shadow-xl active:scale-95 flex items-center justify-center gap-2"
                >
                  {isLoading ? <FaSpinner className="animate-spin" /> : "SUBMIT"}
                </button>
              </form>
            </div>
          )}

          {/* Footer Terms */}
          <div className="mt-auto pt-2 md:pt-6 text-center text-[10px] sm:text-[11px] font-medium text-gray-400 leading-relaxed max-w-[900px] mx-auto">
            <p>
              By proceeding, you agree to Cabzigo's{" "}
              <a href="/privacy" className="text-blue-500 hover:underline">Privacy Policy</a>,{" "}
              <a href="/terms" className="text-blue-500 hover:underline">User Agreement</a> and{" "}
              <a href="/terms" className="text-blue-500 hover:underline">T&Cs</a>
            </p>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default LoginModal;
